import React from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { LibraryCard, CardActions, colors } from 'src/components/app-ui'
import useTrackerFunction from 'src/hooks/useTrackerFunction'
import TagChips from 'src/components/tag-chips'
import CollectionToolBar from '../collection-card/components/collection-tool-bar'
import SharedCollectionToolBar from '../shared-collection-card/components/collection-tool-bar'

const DEFAULT_COVER =
  'https://cdn.minicoursegenerator.com/assets/gateway/collection-cover.png'

const ACCESS_ICONS = {
  Public: 'tabler:world',
  'Email verification': 'tabler:mail',
  'Invitation only': 'tabler:link',
  'In-app authentication': 'tabler:key',
  'Paid access': 'tabler:credit-card',
}

const useStyles = makeStyles({
  access: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    border: 0,
    borderRadius: '6px',
    background: colors.hover,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    color: colors.navy,
    whiteSpace: 'nowrap',
  },
  accessIcon: {
    width: 12,
    height: 12,
    flex: 'none',
  },
  creator: {
    fontSize: '13px',
    color: colors.subtle,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
})

const getPathCover = thumbnail => {
  if (!thumbnail) return DEFAULT_COVER
  return thumbnail.includes('https://')
    ? thumbnail
    : `https://cdn.minicoursegenerator.com/${thumbnail}`
}

/**
 * Learning path (collection) card. `shared` renders a collection shared
 * with the user, which has a reduced menu.
 */
const PathLibraryCard = ({ collection, collections, refresh, shared }) => {
  const classes = useStyles()
  const history = useHistory()
  const trackCollectionClick = useTrackerFunction('clicked_collection_card')

  const duplicatingIds = useSelector(
    state => state.collection.duplicatingIds || [],
  )
  const translatingIds = useSelector(
    state => state.courseCollectionList?.translatingIds || [],
  )
  const isTranslating = translatingIds.includes(collection.id)
  const isDuplicating = duplicatingIds.includes(collection.id)
  const busyLabel = isTranslating
    ? 'Translating…'
    : isDuplicating
    ? 'Duplicating…'
    : null

  const openPath = (page = 'mini-courses') => {
    trackCollectionClick()
    history.push(`/collection/${collection.id}/${page}`)
    window.scrollTo(0, 0)
  }

  const courseCount = collection.courseCount || 0
  const kind = `${courseCount} ${courseCount === 1 ? 'COURSE' : 'COURSES'}`

  const footer = shared ? (
    <>
      <span className={classes.creator}>By {collection.creatorName}</span>
      <SharedCollectionToolBar
        collection={collection}
        collections={collections}
        refresh={refresh}
        renderTrigger={({ openMenu }) => <CardActions onMenu={openMenu} />}
      />
    </>
  ) : (
    <CollectionToolBar
      collection={collection}
      collections={collections}
      refresh={refresh}
      renderTrigger={({ openMenu, openAccess, accessLabel }) => (
        <>
          <button type="button" className={classes.access} onClick={openAccess}>
            <Icon
              icon={ACCESS_ICONS[accessLabel] || 'tabler:key'}
              className={classes.accessIcon}
            />
            {accessLabel}
          </button>
          <CardActions
            onAnalytics={() => openPath('analytics')}
            onMenu={openMenu}
          />
        </>
      )}
    />
  )

  return (
    <LibraryCard
      cover={getPathCover(collection.thumbnail)}
      coverKey={`collection:${collection.id}`}
      kind={kind}
      kindIcon="tabler:route"
      title={collection.name}
      tagsSlot={
        collection.tags?.length > 0 && <TagChips tags={collection.tags} />
      }
      busyLabel={busyLabel}
      onClick={() => openPath()}
      footer={footer}
    />
  )
}

export default PathLibraryCard
